import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { addSocialMediaContent } from '../services/firebase';
import { SocialMediaItem } from '../types';
import { CircleCheck } from 'lucide-react';
import { sendSocialMediaSubmissionReviewEmail } from '../services/sendgrid-service';

interface SocialMediaFormProps {
  contentTitle: string | undefined;
  contentId: string | undefined;
  contentType?: string | 'punishment';
}

interface FormState {
  firstName: string;
  email: string;
  link: string;
}

interface FormErrors {
  firstName?: string;
  email?: string;
  link?: string;
}

const SocialMediaForm: React.FC<SocialMediaFormProps> = ({
  contentTitle,
  contentId,
  contentType = 'punishment',
}) => {
  const [form, setForm] = useState<FormState>({
    firstName: '',
    email: '',
    link: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    setErrors({});
    setSuccessMessage('');
    }, [contentId]);

  const detectPlatform = (url: string): string => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('instagram.com')) return 'instagram';
    if (lowerUrl.includes('x.com') || lowerUrl.includes('twitter.com')) return 'x';
    if (lowerUrl.includes('youtube.com')) return 'youtube';
    if (lowerUrl.includes('tiktok.com')) return 'tiktok';
    return 'unknown';
  };

  const socialMediaRegex = /^(https?:\/\/)?(www\.)?(instagram\.com|x\.com|twitter\.com|youtube\.com|tiktok\.com)\/[^\s]+$/i;

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!form.firstName.trim()) {
      newErrors.firstName = 'Name is required.';
    } else if (form.firstName.length > 100) {
      newErrors.firstName = 'Name cannot exceed 100 characters.';
    }

    if (!form.email) {
      newErrors.email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = 'Email is invalid.';
    }

    if (!form.link.trim()) {
      newErrors.link = 'Only Instagram, Twitter/X, YouTube, or TikTok links are allowed.';
    } else if (!socialMediaRegex.test(form.link.trim())) {
      newErrors.link =
        'Only Instagram, Twitter/X, YouTube, or TikTok links are allowed.';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validate()) return;

    const platform = detectPlatform(form.link);

    if (platform === 'unknown') {
      setErrors({ ...errors, link: 'Unsupported platform link.' });
      return;
    }
    
    if(typeof contentId !== undefined && typeof contentTitle !== undefined) {
        try {
            const socialMediaItem: SocialMediaItem = {
                url: form.link,
                type: platform,
                contentId: contentId || '',
                contentTitle: contentTitle || '',
                firstName: form.firstName,
                email: form.email,
                contentType: contentType,
            }
            const response = await addSocialMediaContent(socialMediaItem)
            console.log(response);

            setSuccessMessage('Your link was submitted successfully!');
            setForm({ firstName: '', email: '', link: '' });
            setErrors({});

          const sendEmailAdmin = await sendSocialMediaSubmissionReviewEmail(
            socialMediaItem.email,
            socialMediaItem.contentTitle,
            socialMediaItem.firstName,
            socialMediaItem.url,
            true
          );

          const sendEmailUser = await sendSocialMediaSubmissionReviewEmail(
            socialMediaItem.email,
            socialMediaItem.contentTitle,
            socialMediaItem.firstName,
            socialMediaItem.url,
            false
          );

        } catch (error) {
            console.error('Error submitting form:', error);
        }
    }
    
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.id]: e.target.value });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="drawerHeading">
        {contentTitle}
      </div>
      <div className="drawerHeading">
        Want to share your punishment with the community?
      </div>
      <div className="drawerHeading text-[#00FCB3]">
        Share your link here.
      </div>
      <div className="fldWrap">
        <input
          className="fldStyle"
          id="firstName"
          type="text"
          placeholder="Name"
          value={form.firstName}
          onChange={handleChange}
        />
        <p className="text-red-600">{errors.firstName ? errors.firstName : ''}</p>
      </div>

      <div className="fldWrap">
        <input
          className="fldStyle"
          id="email"
          type="email"
          placeholder="Email address"
          value={form.email}
          onChange={handleChange}
        />
        <p className="text-red-600">{errors.email ? errors.email : ''}</p>
        </div>

      <div className="fldWrap">
        <input
          className="fldStyle"
          id="link"
          type="text"
          placeholder="https://"
          value={form.link}
          onChange={handleChange}
        />
      </div>
      <p className={`${errors.link 
        ? 'text-red-600' 
        : 'text-white'
        }`}>{errors.link ? errors.link : 'Only Instagram, Facebook, Twitter/X, YouTube, or TikTok links are allowed.'}</p>

      <button
        type="submit" 
        className="btnStyle"
      >
        Submit
      </button>

      {successMessage && <p className="text-[#00fcb3] font-roboto font-medium  py-2  text-lg mt-6 flex items-center"> <span><CircleCheck color='#00fcb3'className='mr-2'/></span> <span>{successMessage}</span></p>}
    </form>
  );
};

export default SocialMediaForm;
